import {
  FONT_FAMILIES,
  FONT_SCALES,
  MAX_CELL_TEXT_LENGTH,
  type BingoFontFamily,
  type BingoFontScale,
  type BingoBoardState,
  type MutationMessage,
  type ServerMessage,
} from "../shared/types";
import { BingoBoardView } from "./board-view";
import { copyText, element, formatTime } from "./dom";
import { logOut } from "./site-gate";
import { BingoSocket, type ConnectionStatus } from "./socket";

type PendingAction =
  | { type: "update_cell_text"; cellId: number; text: string; retries: number }
  | { type: "update_cell_style"; cellId: number; fontFamily: BingoFontFamily; fontScale: BingoFontScale; retries: number }
  | { type: "set_cell_completed"; cellId: number; completed: boolean; retries: number }
  | { type: "reset_completions"; retries: number }
  | { type: "clear_board"; retries: number };

type InFlight = { action: PendingAction; mutationId: string };

type CellControls = {
  textarea: HTMLTextAreaElement;
  counter: HTMLSpanElement;
  toggle: HTMLButtonElement;
  status: HTMLSpanElement;
  fontFamily: HTMLSelectElement;
  fontScale: HTMLSelectElement;
};

const FONT_LABELS: Record<BingoFontFamily, string> = {
  sans: "Strak",
  rounded: "Rond",
  serif: "Klassiek",
  mono: "Typemachine",
};

const FONT_SCALE_LABELS: Record<BingoFontScale, string> = {
  75: "Klein (75%)",
  90: "Compact (90%)",
  100: "Normaal (100%)",
  115: "Groot (115%)",
  130: "Extra groot (130%)",
};

function getInitialToken(boardId: string): string | null {
  const hash = new URLSearchParams(location.hash.slice(1));
  const fromUrl = hash.get("token");
  const storageKey = `bingo-admin-token:${boardId}`;
  if (fromUrl) {
    sessionStorage.setItem(storageKey, fromUrl);
    history.replaceState(null, "", location.pathname + location.search);
    return fromUrl;
  }
  return sessionStorage.getItem(storageKey);
}

function createMutation(action: PendingAction, revision: number, mutationId: string): MutationMessage {
  switch (action.type) {
    case "update_cell_text":
      return { ...action, expectedRevision: revision, mutationId };
    case "update_cell_style":
      return { ...action, expectedRevision: revision, mutationId };
    case "set_cell_completed":
      return { ...action, expectedRevision: revision, mutationId };
    case "reset_completions":
      return { type: action.type, expectedRevision: revision, mutationId };
    case "clear_board":
      return { type: action.type, expectedRevision: revision, mutationId };
  }
}

function actionMatchesState(action: PendingAction, state: BingoBoardState): boolean {
  switch (action.type) {
    case "update_cell_text":
      return state.cells[action.cellId]?.text === action.text;
    case "update_cell_style":
      return (
        state.cells[action.cellId]?.fontFamily === action.fontFamily &&
        state.cells[action.cellId]?.fontScale === action.fontScale
      );
    case "set_cell_completed":
      return state.cells[action.cellId]?.completed === action.completed;
    case "reset_completions":
      return state.cells.every((cell) => !cell.completed);
    case "clear_board":
      return state.cells.every((cell) => !cell.completed && cell.text === "");
  }
}

export function renderControlPage(app: HTMLElement, boardId: string): void {
  document.body.className = "app-body control-body";
  document.title = "Bediening · IRL Bingo";

  let token = getInitialToken(boardId);
  let socket: BingoSocket | null = null;
  let boardState: BingoBoardState | null = null;
  let inFlight: InFlight | null = null;
  const queue: PendingAction[] = [];
  const drafts = Array.from({ length: 9 }, () => "");
  const dirty = new Set<number>();
  const debounceTimers = new Map<number, number>();

  const connectionDot = element("span", { className: "connection-pill__dot", attrs: { "aria-hidden": "true" } });
  const connectionText = element("span", { text: "Verbinden…" });
  const connection = element("div", {
    className: "connection-pill is-connecting",
    attrs: { role: "status" },
    children: [connectionDot, connectionText],
  });
  const saveText = element("span", { className: "save-state", text: "Wachten op verbinding" });
  const updatedText = element("span", { className: "updated-time" });
  const preview = new BingoBoardView({ compact: true });
  const controls: CellControls[] = [];

  const copyOverlay = element("button", { className: "button button--secondary button--small", text: "Kopieer overlay-URL" });
  copyOverlay.addEventListener("click", async () => {
    const url = `${location.origin}/overlay/${boardId}`;
    copyOverlay.textContent = (await copyText(url)) ? "Overlay-URL gekopieerd" : "Kopiëren mislukt";
    window.setTimeout(() => (copyOverlay.textContent = "Kopieer overlay-URL"), 1_500);
  });
  const copyControl = element("button", {
    className: "button button--secondary button--small",
    text: "Kopieer beheerlink",
    attrs: { disabled: "" },
  });
  copyControl.addEventListener("click", async () => {
    if (!token) return;
    const url = new URL(`/control/${boardId}`, location.origin);
    url.hash = new URLSearchParams({ token }).toString();
    copyControl.textContent = (await copyText(url.toString())) ? "Beheerlink gekopieerd" : "Kopiëren mislukt";
    window.setTimeout(() => (copyControl.textContent = "Kopieer beheerlink"), 1_500);
  });
  const logout = element("button", { className: "button button--ghost button--small", text: "Uitloggen" });
  logout.addEventListener("click", () => void logOut());
  const header = element("header", {
    className: "control-header",
    children: [
      element("div", {
        children: [
          element("p", { className: "eyebrow", text: "LIVE CONTROL" }),
          element("h1", { className: "control-title", text: "Bingokaart bedienen" }),
        ],
      }),
      element("div", { className: "control-header__actions", children: [connection, copyControl, copyOverlay, logout] }),
    ],
  });

  const controlsGrid = element("div", { className: "control-grid" });
  for (let index = 0; index < 9; index += 1) {
    const label = element("label", {
      className: "cell-editor__label",
      text: `Vak ${index + 1}`,
      attrs: { for: `cell-text-${index}` },
    });
    const counter = element("span", { className: "cell-editor__counter", text: `0 / ${MAX_CELL_TEXT_LENGTH}` });
    const labelRow = element("div", { className: "cell-editor__label-row", children: [label, counter] });
    const textarea = element("textarea", {
      className: "cell-editor__textarea",
      attrs: {
        id: `cell-text-${index}`,
        maxlength: String(MAX_CELL_TEXT_LENGTH),
        rows: "3",
        placeholder: "Wat moet er gebeuren?",
        disabled: "",
      },
    });
    const fontFamily = element("select", {
      className: "cell-editor__select",
      attrs: { id: `cell-font-${index}`, disabled: "", "aria-label": `Lettertype vak ${index + 1}` },
      children: FONT_FAMILIES.map((value) => element("option", { text: FONT_LABELS[value], attrs: { value } })),
    });
    const fontScale = element("select", {
      className: "cell-editor__select",
      attrs: { id: `cell-size-${index}`, disabled: "", "aria-label": `Tekstgrootte vak ${index + 1}` },
      children: FONT_SCALES.map((value) =>
        element("option", { text: FONT_SCALE_LABELS[value], attrs: { value: String(value) } }),
      ),
    });
    const typography = element("div", {
      className: "cell-editor__typography",
      children: [
        element("label", {
          className: "cell-editor__style-field",
          attrs: { for: `cell-font-${index}` },
          children: [element("span", { text: "Lettertype" }), fontFamily],
        }),
        element("label", {
          className: "cell-editor__style-field",
          attrs: { for: `cell-size-${index}` },
          children: [element("span", { text: "Grootte" }), fontScale],
        }),
      ],
    });
    const status = element("span", { className: "cell-editor__status", text: "Open" });
    const toggle = element("button", {
      className: "complete-toggle",
      attrs: { type: "button", disabled: "", "aria-pressed": "false" },
      children: [element("span", { className: "complete-toggle__icon", text: "✓" }), status],
    });
    const hint = element("span", { className: "cell-editor__shortcut", text: `Toets ${index + 1}` });
    const footer = element("div", { className: "cell-editor__footer", children: [toggle, hint] });
    const card = element("section", { className: "cell-editor", children: [labelRow, textarea, typography, footer] });
    controlsGrid.append(card);
    controls.push({ textarea, counter, toggle, status, fontFamily, fontScale });

    textarea.addEventListener("input", () => {
      drafts[index] = textarea.value;
      dirty.add(index);
      counter.textContent = `${textarea.value.length} / ${MAX_CELL_TEXT_LENGTH}`;
      const existingTimer = debounceTimers.get(index);
      if (existingTimer !== undefined) window.clearTimeout(existingTimer);
      debounceTimers.set(
        index,
        window.setTimeout(() => {
          debounceTimers.delete(index);
          enqueue({ type: "update_cell_text", cellId: index, text: drafts[index] ?? "", retries: 0 });
        }, 400),
      );
    });
    textarea.addEventListener("blur", () => {
      const timer = debounceTimers.get(index);
      if (timer === undefined) return;
      window.clearTimeout(timer);
      debounceTimers.delete(index);
      enqueue({ type: "update_cell_text", cellId: index, text: drafts[index] ?? "", retries: 0 });
    });
    toggle.addEventListener("click", () => {
      if (!boardState || hasPendingCompletion(index)) return;
      enqueue({
        type: "set_cell_completed",
        cellId: index,
        completed: !(boardState.cells[index]?.completed ?? false),
        retries: 0,
      });
    });
    const updateTypography = (): void => {
      enqueue({
        type: "update_cell_style",
        cellId: index,
        fontFamily: fontFamily.value as BingoFontFamily,
        fontScale: Number(fontScale.value) as BingoFontScale,
        retries: 0,
      });
    };
    fontFamily.addEventListener("change", updateTypography);
    fontScale.addEventListener("change", updateTypography);
  }

  const resetButton = element("button", { className: "button button--secondary", text: "Alle vinkjes resetten" });
  const clearButton = element("button", { className: "button button--danger", text: "Hele kaart leegmaken" });
  resetButton.addEventListener("click", () => enqueue({ type: "reset_completions", retries: 0 }));
  clearButton.addEventListener("click", () => {
    if (!window.confirm("Weet je zeker dat je alle teksten én vinkjes wilt wissen? Dit kan niet ongedaan worden gemaakt.")) return;
    for (const timer of debounceTimers.values()) window.clearTimeout(timer);
    debounceTimers.clear();
    queue.splice(0, queue.length);
    drafts.fill("");
    for (let index = 0; index < 9; index += 1) dirty.add(index);
    enqueue({ type: "clear_board", retries: 0 });
  });
  const toolbar = element("div", {
    className: "control-toolbar",
    children: [element("div", { className: "save-meta", children: [saveText, updatedText] }), resetButton, clearButton],
  });

  const previewHeader = element("div", {
    className: "preview-panel__header",
    children: [
      element("div", {
        children: [
          element("p", { className: "eyebrow", text: "LIVE PREVIEW" }),
          element("h2", { className: "preview-panel__title", text: "Wat OBS ziet" }),
        ],
      }),
      element("span", { className: "preview-panel__revision", text: "REV 0" }),
    ],
  });
  const previewPanel = element("aside", { className: "preview-panel", children: [previewHeader, preview.element] });
  const editorPanel = element("section", { className: "editor-panel", children: [toolbar, controlsGrid] });
  const body = element("div", { className: "control-layout", children: [editorPanel, previewPanel] });
  const shell = element("main", { className: "control-shell", children: [header, body] });
  const tokenGate = element("div", { className: "token-gate" });
  app.replaceChildren(shell, tokenGate);

  function updateConnection(status: ConnectionStatus): void {
    connection.className = `connection-pill is-${status}`;
    connectionText.textContent =
      status === "connected"
        ? "Verbonden"
        : status === "reconnecting"
          ? "Opnieuw verbinden…"
          : status === "offline"
            ? "Offline"
            : "Verbinden…";
    if (status !== "connected") saveText.textContent = status === "offline" ? "Wijzigingen wachten" : "Synchroniseren…";
    updateUi();
  }

  function hasPendingCompletion(cellId: number): boolean {
    return (
      (inFlight?.action.type === "set_cell_completed" && inFlight.action.cellId === cellId) ||
      queue.some((action) => action.type === "set_cell_completed" && action.cellId === cellId)
    );
  }

  function hasPendingStyle(cellId: number): boolean {
    return (
      (inFlight?.action.type === "update_cell_style" && inFlight.action.cellId === cellId) ||
      queue.some((action) => action.type === "update_cell_style" && action.cellId === cellId)
    );
  }

  function enqueue(action: PendingAction): void {
    if (action.type === "update_cell_text" || action.type === "update_cell_style") {
      const index = queue.findIndex((item) => item.type === action.type && item.cellId === action.cellId);
      if (index >= 0) queue[index] = action;
      else queue.push(action);
    } else {
      queue.push(action);
    }
    saveText.textContent = "Opslaan…";
    processQueue();
    updateUi();
  }

  function processQueue(): void {
    if (inFlight || !boardState || !socket?.canWrite) return;
    const action = queue.shift();
    if (!action) {
      saveText.textContent = "Alles opgeslagen";
      updateUi();
      return;
    }
    const mutationId = crypto.randomUUID();
    if (socket.send(createMutation(action, boardState.revision, mutationId))) {
      inFlight = { action, mutationId };
      saveText.textContent = "Opslaan…";
    } else {
      queue.unshift(action);
    }
    updateUi();
  }

  function finishInFlight(): void {
    const action = inFlight?.action;
    inFlight = null;
    if (action?.type === "update_cell_text" && drafts[action.cellId] === action.text) dirty.delete(action.cellId);
    if (action?.type === "clear_board") dirty.clear();
    saveText.textContent = queue.length ? "Opslaan…" : "Alles opgeslagen";
    updateUi();
    processQueue();
  }

  function applyState(state: BingoBoardState, message?: Extract<ServerMessage, { type: "state" }>): void {
    boardState = state;
    preview.render(state, message?.reason ?? "reconnect", message?.change);
    const revision = previewHeader.querySelector<HTMLElement>(".preview-panel__revision");
    if (revision) revision.textContent = `REV ${state.revision}`;
    updatedText.textContent = `Laatst bijgewerkt ${formatTime(state.updatedAt)}`;

    if (inFlight && message?.mutationId === inFlight.mutationId) {
      updateUi();
      finishInFlight();
      return;
    }
    if (inFlight && message?.reason === "reconnect") {
      if (actionMatchesState(inFlight.action, state)) finishInFlight();
      else retryInFlight();
      return;
    }
    updateUi();
    processQueue();
  }

  function retryInFlight(): void {
    if (!inFlight || !boardState || !socket?.canWrite) return;
    if (inFlight.action.retries >= 3) {
      saveText.textContent = "Opslaan mislukt";
      inFlight = null;
      updateUi();
      return;
    }
    inFlight.action.retries += 1;
    const mutationId = crypto.randomUUID();
    if (socket.send(createMutation(inFlight.action, boardState.revision, mutationId))) inFlight.mutationId = mutationId;
  }

  function updateUi(): void {
    for (let index = 0; index < controls.length; index += 1) {
      const control = controls[index];
      const model = boardState?.cells[index];
      if (!control || !model) continue;
      if (!dirty.has(index) && control.textarea.value !== model.text) {
        drafts[index] = model.text;
        control.textarea.value = model.text;
      }
      control.counter.textContent = `${control.textarea.value.length} / ${MAX_CELL_TEXT_LENGTH}`;
      control.textarea.disabled = !socket?.canWrite;
      if (!hasPendingStyle(index)) {
        control.fontFamily.value = model.fontFamily;
        control.fontScale.value = String(model.fontScale);
      }
      control.fontFamily.disabled = !socket?.canWrite;
      control.fontScale.disabled = !socket?.canWrite;
      const pending = hasPendingCompletion(index);
      control.toggle.disabled = !socket?.canWrite || pending;
      control.toggle.classList.toggle("is-completed", model.completed);
      control.toggle.setAttribute("aria-pressed", String(model.completed));
      control.status.textContent = pending ? "Opslaan…" : model.completed ? "Voltooid" : "Markeer voltooid";
    }
    resetButton.disabled = !socket?.canWrite || Boolean(inFlight);
    clearButton.disabled = !socket?.canWrite || Boolean(inFlight);
  }

  function showTokenGate(message = "Open de volledige privé beheerlink of vul het beheer-token in."): void {
    tokenGate.classList.add("is-visible");
    const title = element("h2", { className: "token-card__title", text: "Beheer-token nodig" });
    const description = element("p", { className: "token-card__description", text: message });
    const input = element("input", {
      className: "text-input",
      attrs: { type: "password", autocomplete: "off", placeholder: "Plak het beheer-token" },
    });
    const submit = element("button", { className: "button button--primary button--wide", text: "Kaart ontgrendelen" });
    const form = element("form", { className: "token-card", children: [title, description, input, submit] });
    tokenGate.replaceChildren(form);
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      const supplied = input.value.trim();
      if (!supplied) return;
      token = supplied;
      sessionStorage.setItem(`bingo-admin-token:${boardId}`, supplied);
      copyControl.disabled = true;
      tokenGate.classList.remove("is-visible");
      connect();
    });
  }

  function handleMessage(message: ServerMessage): void {
    if (message.type === "authenticated") {
      copyControl.disabled = false;
      return;
    }
    if (message.type === "state") {
      applyState(message.state, message);
      return;
    }
    if (message.type !== "error") return;

    if (message.code === "unauthorized") {
      socket?.stop();
      sessionStorage.removeItem(`bingo-admin-token:${boardId}`);
      token = null;
      copyControl.disabled = true;
      showTokenGate("Dit beheer-token is niet geldig. Controleer de privé beheerlink.");
      return;
    }
    if (message.state) applyState(message.state);
    if (inFlight && message.mutationId === inFlight.mutationId) {
      if (message.code === "revision_conflict") retryInFlight();
      else if (message.code === "rate_limited") window.setTimeout(retryInFlight, 2_000);
      else {
        saveText.textContent = message.message;
        inFlight = null;
        updateUi();
      }
    }
  }

  function connect(): void {
    socket?.stop();
    if (!token) {
      showTokenGate();
      return;
    }
    socket = new BingoSocket({ boardId, mode: "control", token, onStatus: updateConnection, onMessage: handleMessage });
    socket.start();
  }

  document.addEventListener("keydown", (event) => {
    const target = event.target;
    if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || (target instanceof HTMLElement && target.isContentEditable)) return;
    const cellId = Number(event.key) - 1;
    if (cellId >= 0 && cellId < 9) controls[cellId]?.toggle.click();
  });

  window.addEventListener("pagehide", () => {
    socket?.stop();
    preview.destroy();
  });
  connect();
}
